import { Dialog } from '../components/Dialog';
import type { GameStore } from '../../store/GameStore';
import { SaveManager, InMemoryStorage, IndexedDBStorage } from '../../persistence/SaveManager';
import { formatCurrency } from '../../i18n/i18n';
import { syncAllCounters } from '../../core/entities/syncCounters';

// Use IndexedDB in browser, InMemory as fallback
function createSaveManager(): SaveManager {
  try {
    if (typeof indexedDB !== 'undefined') {
      return new SaveManager(new IndexedDBStorage());
    }
  } catch {
    // fallback
  }
  return new SaveManager(new InMemoryStorage());
}

const saveManager = createSaveManager();

/**
 * Dialog for saving and loading games.
 * 3 manual slots + 1 autosave slot.
 */
export function openSaveLoadDialog(
  parent: HTMLElement,
  store: GameStore,
  mode: 'save' | 'load',
  onClose: () => void,
  onLoad?: () => void,
): void {
  const prevSpeed = store.getState().speed;
  store.dispatch({ type: 'SET_SPEED', speed: 0 });

  const dialog = new Dialog({
    title: mode === 'save' ? 'Save Game' : 'Load Game',
    onClose: () => {
      store.dispatch({ type: 'SET_SPEED', speed: prevSpeed });
      onClose();
    },
  });

  const container = document.createElement('div');
  container.style.cssText = 'display:flex;flex-direction:column;gap:8px;min-width:320px;';

  const statusEl = document.createElement('div');
  statusEl.style.cssText = 'font-size:12px;color:var(--color-text-muted);min-height:20px;text-align:center;';

  async function renderSlots() {
    container.innerHTML = '';
    const slots = await saveManager.listSlots();

    for (const slot of slots) {
      const card = document.createElement('div');
      card.className = 'card';
      card.style.cssText = 'display:flex;justify-content:space-between;align-items:center;gap:12px;';

      const info = document.createElement('div');
      info.style.cssText = 'flex:1;min-width:0;';

      const slotLabel = slot.slot === 0 ? 'Autosave' : `Slot ${slot.slot}`;
      const labelEl = document.createElement('strong');
      labelEl.textContent = slotLabel;
      labelEl.style.fontSize = '13px';
      info.appendChild(labelEl);

      if (!slot.isEmpty) {
        const details = document.createElement('div');
        details.style.cssText = 'font-size:11px;color:var(--color-text-secondary);margin-top:2px;';
        details.textContent = `${slot.companyName} — Year ${slot.year}, Week ${slot.week} — ${formatCurrency(slot.funds)}`;
        info.appendChild(details);

        const timeEl = document.createElement('div');
        timeEl.style.cssText = 'font-size:10px;color:var(--color-text-muted);';
        timeEl.textContent = new Date(slot.timestamp).toLocaleString();
        info.appendChild(timeEl);
      } else {
        const emptyEl = document.createElement('div');
        emptyEl.style.cssText = 'font-size:11px;color:var(--color-text-muted);';
        emptyEl.textContent = 'Empty';
        info.appendChild(emptyEl);
      }

      card.appendChild(info);

      const btnContainer = document.createElement('div');
      btnContainer.style.cssText = 'display:flex;gap:4px;flex-shrink:0;';

      if (mode === 'save' && slot.slot > 0) {
        const saveBtn = document.createElement('button');
        saveBtn.className = 'btn btn--primary';
        saveBtn.textContent = 'Save';
        saveBtn.style.fontSize = '11px';
        saveBtn.addEventListener('click', async () => {
          statusEl.textContent = 'Saving...';
          try {
            await saveManager.save(
              slot.slot,
              store.getState(),
              store.getRng().getState(),
              'game-studio',
            );
            statusEl.textContent = 'Saved!';
            statusEl.style.color = 'var(--color-success)';
            await renderSlots();
          } catch (e) {
            statusEl.textContent = `Save failed: ${e}`;
            statusEl.style.color = 'var(--color-danger)';
          }
        });
        btnContainer.appendChild(saveBtn);
      }

      if (mode === 'load' && !slot.isEmpty) {
        const loadBtn = document.createElement('button');
        loadBtn.className = 'btn btn--success';
        loadBtn.textContent = 'Load';
        loadBtn.style.fontSize = '11px';
        loadBtn.addEventListener('click', async () => {
          statusEl.textContent = 'Loading...';
          try {
            const data = await saveManager.load(slot.slot);
            if (data) {
              const { SeededRandom } = await import('../../core/rng/SeededRandom');
              const newRng = new SeededRandom(0);
              newRng.setState(data.rngState);
              store.replaceState(data.core, newRng);
              syncAllCounters(data.core);
              statusEl.textContent = 'Loaded!';
              dialog.close();
              onLoad?.();
            } else {
              statusEl.textContent = 'Failed to load save.';
              statusEl.style.color = 'var(--color-danger)';
            }
          } catch (e) {
            statusEl.textContent = `Load failed: ${e}`;
            statusEl.style.color = 'var(--color-danger)';
          }
        });
        btnContainer.appendChild(loadBtn);
      }

      if (!slot.isEmpty && slot.slot > 0) {
        const deleteBtn = document.createElement('button');
        deleteBtn.className = 'btn';
        deleteBtn.textContent = '×';
        deleteBtn.style.cssText = 'font-size:11px;color:var(--color-danger);';
        deleteBtn.setAttribute('aria-label', 'Delete save');
        deleteBtn.addEventListener('click', async () => {
          await saveManager.deleteSlot(slot.slot);
          await renderSlots();
        });
        btnContainer.appendChild(deleteBtn);
      }

      card.appendChild(btnContainer);
      container.appendChild(card);
    }

    container.appendChild(statusEl);
  }

  renderSlots();
  dialog.body.appendChild(container);
  dialog.mount(parent);
}

/** Autosave the current game. */
export async function autosave(store: GameStore): Promise<void> {
  try {
    await saveManager.save(
      0,
      store.getState(),
      store.getRng().getState(),
      'game-studio',
    );
  } catch (e) {
    console.error('Autosave failed:', e);
  }
}

/** Check if any saves exist. */
export async function hasSaves(): Promise<boolean> {
  const slots = await saveManager.listSlots();
  return slots.some((s) => !s.isEmpty);
}

/**
 * Standalone load dialog for the title screen — no GameStore needed.
 * Returns loaded save data via callback, or calls onCancel if closed.
 */
export function openLoadFromTitle(
  parent: HTMLElement,
  onLoaded: (data: import('../../core/types').SaveData) => void,
  onCancel: () => void,
): void {
  const dialog = new Dialog({
    title: 'Load Game',
    onClose: onCancel,
  });

  const container = document.createElement('div');
  container.style.cssText = 'display:flex;flex-direction:column;gap:8px;min-width:320px;';

  const statusEl = document.createElement('div');
  statusEl.style.cssText = 'font-size:12px;color:var(--color-text-muted);min-height:20px;text-align:center;';

  async function renderSlots() {
    container.innerHTML = '';
    const slots = await saveManager.listSlots();

    for (const slot of slots) {
      if (slot.isEmpty) continue;

      const card = document.createElement('div');
      card.className = 'card';
      card.style.cssText = 'display:flex;justify-content:space-between;align-items:center;gap:12px;cursor:pointer;';

      const info = document.createElement('div');
      info.style.cssText = 'flex:1;min-width:0;';
      const slotLabel = slot.slot === 0 ? 'Autosave' : `Slot ${slot.slot}`;
      const labelEl = document.createElement('strong');
      labelEl.textContent = slotLabel;
      labelEl.style.fontSize = '13px';
      info.appendChild(labelEl);

      const details = document.createElement('div');
      details.style.cssText = 'font-size:11px;color:var(--color-text-secondary);margin-top:2px;';
      details.textContent = `${slot.companyName} — Year ${slot.year}, Week ${slot.week} — ${formatCurrency(slot.funds)}`;
      info.appendChild(details);

      const timeEl = document.createElement('div');
      timeEl.style.cssText = 'font-size:10px;color:var(--color-text-muted);';
      timeEl.textContent = new Date(slot.timestamp).toLocaleString();
      info.appendChild(timeEl);

      card.appendChild(info);

      const loadBtn = document.createElement('button');
      loadBtn.className = 'btn btn--success';
      loadBtn.textContent = 'Load';
      loadBtn.style.fontSize = '11px';
      loadBtn.addEventListener('click', async () => {
        statusEl.textContent = 'Loading...';
        try {
          const data = await saveManager.load(slot.slot);
          if (data) {
            dialog.close();
            onLoaded(data);
          } else {
            statusEl.textContent = 'Failed to load.';
            statusEl.style.color = 'var(--color-danger)';
          }
        } catch (e) {
          statusEl.textContent = `Load failed: ${e}`;
          statusEl.style.color = 'var(--color-danger)';
        }
      });
      card.appendChild(loadBtn);

      container.appendChild(card);
    }

    if (container.children.length === 0) {
      const empty = document.createElement('p');
      empty.textContent = 'No saves found.';
      empty.style.cssText = 'color:var(--color-text-muted);text-align:center;padding:16px;';
      container.appendChild(empty);
    }

    container.appendChild(statusEl);
  }

  renderSlots();
  dialog.body.appendChild(container);
  dialog.mount(parent);
}
